import { FormControlLabel, Checkbox, Slider } from "@mui/material";
import { ButtonText, PrimaryButton } from "components/common/Buttons";
import { CommonText, ErrorText } from "components/common/text/text.component";
import { RouteToolsButtonsWrapper } from "pages/Map/MapPage.style";
import { constrainPointIndexRange, movePointIndexRange, PointIndexRange } from "pages/Map/map-history.utilities";
import React, { useEffect, useRef, useState } from "react";
import style from "./route-tools.module.css";

interface RouteToolsProps {
  toggleDeviceRoute: () => void;
  showRoute: boolean;
  valueLabelFormat: (value: number, index: number) => React.ReactNode;
  onChange: (value: PointIndexRange) => void;
  onChangeCommitted: (value: PointIndexRange) => void;
  sliderRange: PointIndexRange;
  sliderBounds: PointIndexRange;
  pointTimes: number[];
  maxSelectedPoints: number;
  downloadMapHistory: () => void;
  isWindowLoading: boolean;
  isDownloading: boolean;
  downloadProgress?: string;
  downloadError?: string;
}

export function RouteTools({
  sliderRange,
  sliderBounds,
  pointTimes,
  maxSelectedPoints,
  onChange,
  onChangeCommitted,
  toggleDeviceRoute,
  showRoute,
  valueLabelFormat,
  downloadMapHistory,
  isWindowLoading,
  isDownloading,
  downloadProgress,
  downloadError,
}: RouteToolsProps): JSX.Element {
  const [previewRange, setPreviewRange] = useState<PointIndexRange>(sliderRange);
  const [isDragging, setIsDragging] = useState(false);
  const dragState = useRef<{
    pointerId: number;
    startX: number;
    startRange: PointIndexRange;
    latestRange: PointIndexRange;
    railWidth: number;
    animationFrame?: number;
  }>();
  const latestThumbRange = useRef<PointIndexRange>();
  const isInteracting = useRef(false);

  useEffect(() => {
    if (!isInteracting.current) {
      setPreviewRange(sliderRange);
    }
  }, [sliderRange]);

  useEffect(
    () => () => {
      if (dragState.current?.animationFrame !== undefined) {
        window.cancelAnimationFrame(dragState.current.animationFrame);
      }
    },
    [],
  );

  const formattedDateLabel = (pointIndex: number, thumbIndex: number) => {
    const timestamp = pointTimes[pointIndex];
    return Number.isFinite(timestamp) ? valueLabelFormat(timestamp, thumbIndex) : "Loading...";
  };

  const rangesMatch = (first: PointIndexRange, second: PointIndexRange) =>
    first[0] === second[0] && first[1] === second[1];

  const startTrackDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const target = event.target as Element;
    const isSelectedTrack = Boolean(target.closest?.(".MuiSlider-track"));
    const isThumb = Boolean(target.closest?.(".MuiSlider-thumb"));
    const rail = event.currentTarget.querySelector<HTMLElement>(".MuiSlider-rail");

    if (isThumb) {
      isInteracting.current = true;
      setIsDragging(true);
      return;
    }

    if (!isSelectedTrack || !rail || sliderBounds[0] === sliderBounds[1]) {
      return;
    }

    const railWidth = rail.getBoundingClientRect().width;
    if (!railWidth) {
      return;
    }

    dragState.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startRange: previewRange,
      latestRange: previewRange,
      railWidth,
    };
    isInteracting.current = true;
    setIsDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
    event.stopPropagation();
  };

  const moveTrack = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragState.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }

    const fullIndexSpan = sliderBounds[1] - sliderBounds[0];
    const delta = Math.round(((event.clientX - drag.startX) / drag.railWidth) * fullIndexSpan);
    const nextRange = movePointIndexRange(drag.startRange, delta, sliderBounds);
    if (rangesMatch(nextRange, drag.latestRange)) {
      event.preventDefault();
      return;
    }

    drag.latestRange = nextRange;
    if (drag.animationFrame === undefined) {
      drag.animationFrame = window.requestAnimationFrame(() => {
        drag.animationFrame = undefined;
        if (dragState.current === drag) {
          setPreviewRange(drag.latestRange);
        }
      });
    }
    event.preventDefault();
  };

  const commitTrack = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragState.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }

    if (drag.animationFrame !== undefined) {
      window.cancelAnimationFrame(drag.animationFrame);
    }
    dragState.current = undefined;
    isInteracting.current = false;
    setIsDragging(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setPreviewRange(drag.latestRange);
    onChange(drag.latestRange);
    onChangeCommitted(drag.latestRange);
    event.preventDefault();
  };

  const cancelTrack = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragState.current;
    if (drag?.pointerId === event.pointerId) {
      if (drag.animationFrame !== undefined) {
        window.cancelAnimationFrame(drag.animationFrame);
      }
      dragState.current = undefined;
      isInteracting.current = false;
      setIsDragging(false);
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      setPreviewRange(drag.startRange);
    } else if (isInteracting.current) {
      latestThumbRange.current = undefined;
      isInteracting.current = false;
      setIsDragging(false);
      setPreviewRange(sliderRange);
    }
  };

  return (
    <>
      <RouteToolsButtonsWrapper>
        <FormControlLabel
          control={<Checkbox checked={showRoute} onChange={toggleDeviceRoute} />}
          label={<CommonText>Show Device Route</CommonText>}
        />
      </RouteToolsButtonsWrapper>
      <div className={style.markerWrapper}>
        <div className={style.mapToolsWrapper}>
          <div
            className={`${style.sliderContainer} ${isDragging ? style.dragging : ""}`}
            onPointerDownCapture={startTrackDrag}
            onPointerMove={moveTrack}
            onPointerUp={commitTrack}
            onPointerCancel={cancelTrack}
          >
            <Slider
              getAriaLabel={() => "Route"}
              getAriaValueText={(value, index) => `${formattedDateLabel(value, index)}`}
              valueLabelDisplay="auto"
              valueLabelFormat={(value, index) => formattedDateLabel(value, index)}
              step={1}
              onChange={(_event, value, activeThumb) => {
                const constrainedRange = constrainPointIndexRange(
                  value as PointIndexRange,
                  sliderBounds,
                  maxSelectedPoints,
                  activeThumb,
                );
                latestThumbRange.current = constrainedRange;
                isInteracting.current = true;
                setIsDragging(true);
                setPreviewRange(constrainedRange);
              }}
              onChangeCommitted={() => {
                const committedRange = latestThumbRange.current ?? previewRange;
                latestThumbRange.current = undefined;
                isInteracting.current = false;
                setIsDragging(false);
                onChange(committedRange);
                onChangeCommitted(committedRange);
              }}
              value={previewRange}
              disableSwap
              disabled={sliderBounds[0] === sliderBounds[1]}
              min={sliderBounds[0]}
              max={sliderBounds[1]}
            />
            <div className={style.sliderLabelContainer}>
              <div>
                <CommonText>Route Start</CommonText>
                <CommonText>{formattedDateLabel(previewRange[0], 0)}</CommonText>
              </div>
              <div className={style.endLabel}>
                <CommonText>Route End</CommonText>
                <CommonText>{formattedDateLabel(previewRange[1], 1)}</CommonText>
              </div>
            </div>
          </div>
          <div className={style.downloadContainer}>
            <PrimaryButton onClick={downloadMapHistory} disabled={isDownloading}>
              {isDownloading ? "Downloading..." : ButtonText.Download_Data}
            </PrimaryButton>
            <div className={style.statusContainer} aria-live="polite">
              {isWindowLoading && <CommonText>Loading selected history...</CommonText>}
              {isDownloading && downloadProgress && <CommonText>{downloadProgress}</CommonText>}
              {downloadError && <ErrorText>{downloadError}</ErrorText>}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
